import { compareModelsByFamily } from '../utils/modelFamily';
import React from 'react';
import { Modal, Button, Select, Checkbox, Tooltip, Tag, Alert, Input } from 'antd';
import {
  DownOutlined,
  QuestionCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
  isLanguageBeta,
  getQuotationStyle,
  hasQuotationOptions,
  getDefaultQuotationStyle,
  getQuotationTooltip,
  BetaTag,
} from '../LanguageConfig';
import RecommendedTag from '../components/RecommendedTag'

type DemoMetadataBetaFeedbackIssueType = 'quality' | 'style' | 'formatting' | 'terminology' | 'other';

interface TranslateState {
  title: boolean;
  series: boolean;
  blurb: boolean;
}

interface DemoMetadataTranslationModalProps {
  visible: boolean;
  onCancel: () => void;
  onTranslate: () => void;
  bookLanguage: string;
  targetLanguage: string;
  setTargetLanguage: (value: string) => void;
  translateMetadata: TranslateState;
  setTranslateMetadata: (v: TranslateState) => void;
  languages: string[];
  quotationStyle: string;
  setQuotationStyle: (v: string) => void;
  resetCounter: number;
}

const DemoMetadataTranslationModal: React.FC<DemoMetadataTranslationModalProps> = ({
  visible,
  onCancel,
  onTranslate,
  bookLanguage,
  targetLanguage,
  setTargetLanguage,
  translateMetadata,
  setTranslateMetadata,
  languages,
  quotationStyle,
  setQuotationStyle,
  resetCounter,
}) => {
  // AI Models: mirror DemoTranslationModal models, tags, and tier logic
  type Tier = 'Good' | 'Better' | 'Best';

  type DemoModel = {
    id: string;
    label: string;
    tags?: string[];
    defaultTier?: Tier;
    languageTierOverrides?: Record<string, Tier>;
  };

  const getEffectiveTier = (model: DemoModel, lang?: string): Tier | undefined => {
    if (lang && model.languageTierOverrides && model.languageTierOverrides[lang]) {
      return model.languageTierOverrides[lang];
    }
    return model.defaultTier;
  };

  const HARD_MODELS: DemoModel[] = [
    { id: 'Chat-GPT', label: 'Chat-GPT', tags: ['Legacy'] },
    {
      id: 'ChatGPT4.1',
      label: 'ChatGPT 4.1',
      defaultTier: 'Better',
      languageTierOverrides: {
        Ukrainian: 'Good',
        Polish: 'Good',
        'Brazilian Portuguese': 'Best',
      },
    },
    {
      id: 'ChatGPT5',
      label: 'ChatGPT 5',
      defaultTier: 'Best',
    },
    { id: 'Claude', label: 'Claude 3.5', tags: ['Legacy'] },
    { id: 'Claude3', label: 'Claude 3', tags: ['Legacy'] },
    {
      id: 'Claude3.7',
      label: 'Claude 3.7',
      defaultTier: 'Better',
      languageTierOverrides: {
        German: 'Best',
        'Brazilian Portuguese': 'Best',
      },
    },
    { id: 'DeepSeek', label: 'DeepSeek', tags: ['Legacy', '50% OFF'] },
    {
      id: 'DeepSeekR10528',
      label: 'DeepSeek R1',
      tags: ['50% OFF'],
      defaultTier: 'Good',
    },
    {
      id: 'Gemini2.5Pro',
      label: 'Gemini 2.5 Pro',
      defaultTier: 'Best',
      languageTierOverrides: {
        German: 'Best',
        'Brazilian Portuguese': 'Good',
      },
    },
  ];

  const ALLOWED_MODEL_IDS = new Set(['ChatGPT5', 'Gemini2.5Pro', 'Claude3.7']);
  const ALLOWED_LANGUAGES = new Set(['French', 'German']);

  const sortedModels = React.useMemo(() => {
    const arr = [...HARD_MODELS];
    arr.sort((a, b) => compareModelsByFamily(
      { ...a, displayName: a.label },
      { ...b, displayName: b.label }
    ));
    return arr;
  }, [targetLanguage]);

  const renderModelLabel = (m: DemoModel) => {
    return (
      <span>
        {m.label}
        {(m.tags || []).map((t) => {
          const isDiscount = t.toLowerCase().includes('off');
          const isLegacy = t.toLowerCase().includes('legacy');
          if (isLegacy) {
            return (
              <Tag
                key={t}
                style={{ backgroundColor: '#ffd6e7', color: '#9e1068', borderColor: '#9e1068', marginLeft: 8 }}
              >
                {t}
              </Tag>
            );
          }
          return (
            <Tag key={t} color={isDiscount ? 'green' : 'blue'} style={{ marginLeft: 8 }}>
              {t}
            </Tag>
          );
        })}
      </span>
    );
  };

  const [selectedAIModel, setSelectedAIModel] = React.useState<string>('');
  const [isBetaFeedbackModalVisible, setIsBetaFeedbackModalVisible] = React.useState<boolean>(false);
  const [betaFeedbackIssueType, setBetaFeedbackIssueType] = React.useState<DemoMetadataBetaFeedbackIssueType>('quality');
  const [betaFeedbackText, setBetaFeedbackText] = React.useState<string>('');
  const [betaFeedbackContactEmail, setBetaFeedbackContactEmail] = React.useState<string>('');
  const [betaFeedbackIncludeContext, setBetaFeedbackIncludeContext] = React.useState<boolean>(true);
  const [betaFeedbackSubmitting, setBetaFeedbackSubmitting] = React.useState<boolean>(false);
  const [betaFeedbackError, setBetaFeedbackError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!targetLanguage) return;
    const current = HARD_MODELS.find((m) => m.id === selectedAIModel);
    const currentAllowed = !!current && ALLOWED_MODEL_IDS.has(current.id);
    if (currentAllowed) return;

    const findByTier = (tier: Tier) =>
      HARD_MODELS.find(
        (m) => ALLOWED_MODEL_IDS.has(m.id) && getEffectiveTier(m, targetLanguage) === tier
      );
    const desired =
      findByTier('Best') ||
      findByTier('Better') ||
      findByTier('Good') ||
      HARD_MODELS.find((m) => ALLOWED_MODEL_IDS.has(m.id));

    if (desired) {
      setSelectedAIModel(desired.id);
    }
  }, [targetLanguage, selectedAIModel]);

  // Demo credits loaded from public JSON
  const [creditBalance, setCreditBalance] = React.useState<number | null>(null);
  const [initialCreditBalance, setInitialCreditBalance] = React.useState<number>(150000);

  React.useEffect(() => {
    let isMounted = true;
    const loadCredits = async () => {
      try {
        const res = await fetch('/demo/credits.json', { cache: 'no-store' });
        if (!res.ok) throw new Error(`Failed to load credits.json: ${res.status}`);
        const data = await res.json();
        if (isMounted && typeof data?.credit_balance === 'number') {
          try {
            const raw = localStorage.getItem('demoCreditBalance');
            const saved = raw ? parseInt(raw, 10) : NaN;
            const start = Number.isFinite(saved) ? saved : data.credit_balance;
            setCreditBalance(start);
            setInitialCreditBalance(data.credit_balance);
          } catch (e) {
            setCreditBalance(data.credit_balance);
            setInitialCreditBalance(data.credit_balance);
          }
        }
      } catch (e) {
        // Fallback to default if fetch fails
        if (isMounted) {
          setCreditBalance(150000);
          setInitialCreditBalance(150000);
        }
      }
    };
    loadCredits();
    return () => {
      isMounted = false;
    };
  }, []);

  // Handle target language change (set default quotation style when available)
  const handleTargetLanguageChange = (value: string) => {
    setTargetLanguage(value);
    const defaultStyle = getDefaultQuotationStyle(value);
    setQuotationStyle(defaultStyle);
  };

  // Quotation style requirement
  const needsQuotationStyle = hasQuotationOptions(targetLanguage) && !quotationStyle;

  // Demo cost calculation (Title: 50, Blurb: 100; Series disabled in demo)
  const calculateDemoCost = React.useCallback(() => {
    let cost = 0;
    if (translateMetadata.title) cost += 50;
    if (translateMetadata.blurb) cost += 100;
    return cost;
  }, [translateMetadata.title, translateMetadata.blurb]);

React.useEffect(() => {
  // Reset demo credits to 150,000 on page reset
  setInitialCreditBalance(150000);
  setCreditBalance(150000);
  try { localStorage.removeItem('demoCreditBalance'); } catch (e) {}
}, [resetCounter]);

React.useEffect(() => {
  if (visible) return;
  setIsBetaFeedbackModalVisible(false);
  setBetaFeedbackIssueType('quality');
  setBetaFeedbackText('');
  setBetaFeedbackContactEmail('');
  setBetaFeedbackIncludeContext(true);
  setBetaFeedbackError(null);
}, [visible]);

const hasEnoughCredits = creditBalance !== null && creditBalance >= calculateDemoCost();
const betaLanguageWarning = Boolean(targetLanguage && isLanguageBeta(targetLanguage));

  const closeBetaFeedbackModal = () => {
    if (betaFeedbackSubmitting) return;
    setIsBetaFeedbackModalVisible(false);
    setBetaFeedbackIssueType('quality');
    setBetaFeedbackText('');
    setBetaFeedbackContactEmail('');
    setBetaFeedbackIncludeContext(true);
    setBetaFeedbackError(null);
  };

  const handleSubmitBetaFeedback = () => {
    const trimmedFeedback = betaFeedbackText.trim();
    const trimmedEmail = betaFeedbackContactEmail.trim();
    if (trimmedFeedback.length < 10) {
      setBetaFeedbackError('Please share a bit more detail (at least 10 characters).');
      return;
    }
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setBetaFeedbackError('Please enter a valid email address or leave it blank.');
      return;
    }

    setBetaFeedbackSubmitting(true);
    setBetaFeedbackError(null);
    try {
      const submission = {
        submittedAt: new Date().toISOString(),
        source: 'demo_metadata_translation_modal_beta',
        targetLanguage,
        issueType: betaFeedbackIssueType,
        feedback: trimmedFeedback,
        contactEmail: trimmedEmail || null,
        includeContext: betaFeedbackIncludeContext,
        context: betaFeedbackIncludeContext
          ? {
              selectedAIModel,
              quotationStyle,
              translateMetadata,
            }
          : null,
      };
      const raw = localStorage.getItem('demoMetadataTranslationBetaFeedback');
      const existing = raw ? JSON.parse(raw) : [];
      const next = Array.isArray(existing) ? [...existing, submission] : [submission];
      localStorage.setItem('demoMetadataTranslationBetaFeedback', JSON.stringify(next.slice(-50)));
      closeBetaFeedbackModal();
    } catch (error) {
      setBetaFeedbackError('Unable to submit feedback right now. Please try again.');
    } finally {
      setBetaFeedbackSubmitting(false);
    }
  };

  const isAnySelected = translateMetadata.title || translateMetadata.blurb; // Series disabled in demo
  const isSubmitEnabled =
    !!targetLanguage && isAnySelected && hasEnoughCredits && !needsQuotationStyle;

  // Deduct credits on translate, then delegate to parent handler
  const handleTranslateClick = () => {
    const cost = calculateDemoCost();
    if (creditBalance === null) return;
    if (creditBalance < cost) return;
    setCreditBalance((prev) => {
      const next = typeof prev === 'number' ? prev - cost : prev;
      try { if (typeof next === 'number') localStorage.setItem('demoCreditBalance', String(next)); } catch (e) {}
      return next;
    });
    onTranslate();
  };

  return (
    <>
    <Modal
      title="Translate Metadata"
      visible={visible}
      onCancel={onCancel}
      width={760}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button
          key="translate"
          type="primary"
          onClick={handleTranslateClick}
          disabled={!isSubmitEnabled}
          data-testid="tutorial:translate-metadata:translate-button"
        >
          Translate
        </Button>,
      ]}
    >
      <div data-testid="tutorial:translate-metadata:modal">
        {/* Origin and Target language */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15, marginBottom: 15 }}>
          <div data-testid="tutorial:translate-metadata:target-language">
            <label style={{ display: 'block', marginBottom: 5 }}>
              <strong>Origin Language:</strong>
            </label>
            <div>{bookLanguage}</div>
          </div>

          <div data-testid="tutorial:translate-metadata:ai-model">
            <label style={{ display: 'block', marginBottom: 5 }}>
              <strong>Target Language:</strong>
            </label>
            <Select
              value={targetLanguage}
              onChange={handleTargetLanguageChange}
              style={{ width: '100%' }}
              placeholder="Select a target language"
              suffixIcon={
                <span className="ant-select-suffix">
                  <DownOutlined style={{ color: '#1677ff', pointerEvents: 'none' }} />
                </span>
              }
            >
              {languages
                .filter((lang) => lang !== bookLanguage)
                .map((lang) => (
                  <Select.Option key={lang} value={lang} disabled={!ALLOWED_LANGUAGES.has(lang)}>
                    <span>
                      {lang}
                      {isLanguageBeta(lang) && <BetaTag />}
                    </span>
                  </Select.Option>
                ))}
            </Select>
            {betaLanguageWarning && targetLanguage && (
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                  <strong>{targetLanguage} translation is currently in beta.</strong>
                  <Button
                    type="link"
                    size="small"
                    style={{ paddingInline: 0, height: 'auto', fontSize: 12 }}
                    onClick={() => {
                      setBetaFeedbackError(null);
                      setIsBetaFeedbackModalVisible(true);
                    }}
                  >
                    Give feedback
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* AI Model + Quotation Style */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15, marginBottom: 12 }}>
          {/* AI Model (demo) */}
          <div data-testid="tutorial:translate-metadata:quotation-style">
            <label style={{ display: 'block', marginBottom: 5 }}>
              <strong>AI Model:</strong>
            </label>
            <Select
              value={targetLanguage ? (selectedAIModel || undefined) : undefined}
              onChange={setSelectedAIModel}
              style={{ width: '100%' }}
              placeholder={targetLanguage ? 'Select an AI Model' : 'Choose a Language to select an AI Model'}
              disabled={!targetLanguage}
              suffixIcon={
                <span className="ant-select-suffix">
                  <DownOutlined style={{ color: '#1677ff', pointerEvents: 'none' }} />
                </span>
              }
            >
              {sortedModels.map((m) => (
                <Select.Option key={m.id} value={m.id} disabled={!ALLOWED_MODEL_IDS.has(m.id)}>
                  {renderModelLabel(m)}
                </Select.Option>
              ))}
            </Select>
          </div>

          {/* Quotation Style */}
          <div>
            <label style={{ display: 'block', marginBottom: 5 }}>
              <strong>Quotation Style:</strong>
              <Tooltip title={getQuotationTooltip(targetLanguage)}>
                <QuestionCircleOutlined
                  style={{ marginLeft: 5, color: '#1677ff', cursor: 'pointer' }}
                />
              </Tooltip>
              {needsQuotationStyle && (
                <Tag color="red" style={{ marginLeft: 8 }}>
                  Required
                </Tag>
              )}
            </label>
            {(() => {
              const quotationConfig = getQuotationStyle(targetLanguage);
              if (!quotationConfig) {
                return (
                  <>
                    <Select disabled value="unknown" style={{ width: '100%' }}>
                      <Select.Option value="unknown"> </Select.Option>
                    </Select>
                    <div style={{ color: '#999', fontSize: 12, marginTop: 5 }}></div>
                  </>
                );
              }
              if (quotationConfig.options) {
                return (
                  <Select
                    value={quotationStyle}
                    onChange={setQuotationStyle}
                    placeholder="Please select a quotation style"
                    style={{
                      width: '100%',
                      ...(needsQuotationStyle ? { borderColor: '#ff4d4f' } : {}),
                    }}
                    status={needsQuotationStyle ? 'error' : undefined}
                    suffixIcon={
                      <span className="ant-select-suffix">
                        <DownOutlined style={{ color: '#1677ff', pointerEvents: 'none' }} />
                      </span>
                    }
                  >
                    {quotationConfig.options.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label} ({option.marks})
                        {option.isDefault && (
                          <RecommendedTag style={{ position: 'relative', top: '-0.5px' }} />
                        )}
                      </Select.Option>
                    ))}
                  </Select>
                );
              }
              if (quotationConfig.fixed) {
                return (
                  <>
                    <Select disabled value="fixed" style={{ width: '100%' }}>
                      <Select.Option value="fixed">
                        {quotationConfig.fixed.name} ({quotationConfig.fixed.marks})
                      </Select.Option>
                    </Select>
                    <div style={{ color: '#999', fontSize: 12, marginTop: 5 }}>
                      Quotation style is fixed for {targetLanguage}
                    </div>
                  </>
                );
              }
            })()}
          </div>
        </div>

        {/* Fields to Translate */}
        <div
          data-testid="tutorial:translate-metadata:fields"
          style={{
            background: '#f7f7f7',
            padding: 12,
            borderRadius: 6,
            marginBottom: 12,
          }}
        >
          <div style={{ marginBottom: 6 }}>
            <strong>Select Fields to Translate:</strong>
          </div>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <Checkbox
              checked={translateMetadata.title}
              onChange={(e) =>
                setTranslateMetadata({ ...translateMetadata, title: e.target.checked })
              }
            >
              <span style={{ fontWeight: 500 }}>Title</span>
            </Checkbox>

            <Checkbox checked={false} disabled>
              <span style={{ fontWeight: 500 }}>Series Name</span>
              <Tooltip title="Series translation disabled in demo">
                <ExclamationCircleOutlined style={{ color: '#CC5500', marginLeft: 8 }} />
              </Tooltip>
            </Checkbox>

            <Checkbox
              checked={translateMetadata.blurb}
              onChange={(e) =>
                setTranslateMetadata({ ...translateMetadata, blurb: e.target.checked })
              }
            >
              <span style={{ fontWeight: 500 }}>Blurb</span>
            </Checkbox>
          </div>
        </div>

        {/* Credit info (compact) */}
        <div
          data-testid="tutorial:translate-metadata:cost-readiness"
          style={{
            background: '#f0f7ff',
            padding: 12,
            borderRadius: 6,
            marginBottom: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <strong>Credit Balance:</strong>
            <span>{creditBalance !== null ? creditBalance : 'Loading...'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
            <strong>Translation Credit Cost:</strong>
            <span style={{ marginLeft: 5, marginRight: 5 }}>{calculateDemoCost()}</span>
            <Tooltip title="Title returns three options (50 credits). Blurb translation costs 100 credits.">
              <QuestionCircleOutlined style={{ color: '#1677ff' }} />
            </Tooltip>
          </div>
        </div>

        {/* Alerts */}
        {!hasEnoughCredits && (
          <Alert
            className="modal-alert-compact"
            message="You do not have enough credits for this translation."
            type="error"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginBottom: 8 }}
          />
        )}
        {needsQuotationStyle && (
          <Alert
            className="modal-alert-compact"
            message={`Please select a quotation style for ${targetLanguage} translation.`}
            type="error"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginBottom: 8 }}
          />
        )}
        {translateMetadata.title && (
          <Alert
            className="modal-alert-compact"
            message="Tip: Title translations work best when Primary Genre, Tropes, Keywords, and Blurb are populated."
            type="info"
            showIcon
            style={{ marginBottom: 0 }}
          />
        )}

        {/* AI Instructions intentionally disabled for demo */}
      </div>
    </Modal>
    <Modal
      title="Share Translation Beta Feedback"
      visible={isBetaFeedbackModalVisible}
      onCancel={closeBetaFeedbackModal}
      onOk={handleSubmitBetaFeedback}
      okText="Submit Feedback"
      confirmLoading={betaFeedbackSubmitting}
      cancelButtonProps={{ disabled: betaFeedbackSubmitting }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div>
          <label style={{ display: 'block', marginBottom: 6 }}>
            <strong>Feedback Topic</strong>
          </label>
          <Select
            value={betaFeedbackIssueType}
            onChange={(value) => setBetaFeedbackIssueType(value as DemoMetadataBetaFeedbackIssueType)}
            style={{ width: '100%' }}
          >
            <Select.Option value="quality">Translation quality</Select.Option>
            <Select.Option value="style">Voice or style</Select.Option>
            <Select.Option value="formatting">Formatting or punctuation</Select.Option>
            <Select.Option value="terminology">Terminology or glossary</Select.Option>
            <Select.Option value="other">Other</Select.Option>
          </Select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 6 }}>
            <strong>What should we improve?</strong>
          </label>
          <Input.TextArea
            value={betaFeedbackText}
            onChange={(event) => setBetaFeedbackText(event.target.value)}
            rows={5}
            maxLength={2000}
            showCount
            placeholder="Tell us what you expected and what happened."
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 6 }}>
            <strong>Contact Email (optional)</strong>
          </label>
          <Input
            value={betaFeedbackContactEmail}
            onChange={(event) => setBetaFeedbackContactEmail(event.target.value)}
            placeholder="you@example.com"
          />
        </div>
        <Checkbox
          checked={betaFeedbackIncludeContext}
          onChange={(event) => setBetaFeedbackIncludeContext(Boolean(event.target.checked))}
        >
          Include current translation settings with this feedback.
        </Checkbox>
        {betaFeedbackError && (
          <Alert
            className="modal-alert-compact"
            type="error"
            showIcon={false}
            message={betaFeedbackError}
          />
        )}
      </div>
    </Modal>
    </>
  );
};

export default DemoMetadataTranslationModal;
